<template>
  <div
    class="order-item"
    :class="{
      'order-item--addon': orderItem.type === 'ADD_ON',
      'order-item--expanded': isExpanded,
    }"
    @click="toggleExpanded"
  >
    <div class="order-item__content">
      <div class="order-item__main">
        <div class="order-item__image">
          <img :src="getImageLink" alt="" />
        </div>
        <div class="order-item__content-info">
          <div class="order-item__title">{{ orderItem.title }}</div>

          <div class="order-item__price">
            {{ formatPriceAmount(orderItem?.total?.amount, locale) }}
          </div>
        </div>
      </div>
      <div v-if="isItemVisible" class="order-item__chevron">
        <IconChevronDown :class="{ 'rotate-180': isExpanded }" />
      </div>
    </div>

    <div v-if="isItemVisible" class="order-item__actions">
      <button
        v-if="orderItem.status !== 'CANCELED'"
        class="order-item__button order-item__button--primary"
        @click.stop="handleDownloadFile"
      >
        <IconFile />
        {{ t('account.orders.downloadPdf') }}
      </button>
      <button
        v-if="orderItem.status !== 'CANCELED' && isOrderAfterInvoiceCutoff"
        class="order-item__button order-item__button--text"
        @click.stop="handleInvoiceAction"
      >
        <IconDownload />
        {{ t('account.orders.generateInvoice') }}
      </button>
      <nuxt-link
        v-if="orderItem?.review_url"
        :to="orderItem?.review_url"
        color="secondary"
        external
        target="_blank"
        class="order-item__button order-item__button--text order-item__button--full-width"
        @click.stop
      >
        <IconReview />
        {{ t('account.orders.review') }}
      </nuxt-link>
    </div>

    <div v-if="orderItem?.status === 'CANCELED'" class="order-item__canceled">
      {{ t('account.orders.canceled') }}
    </div>
  </div>

  <InvoiceInformationSidebar
    :is-open="isInvoiceSidebarOpen"
    :order-id="orderId"
    :order-item-id="orderItem.id"
    @close="closeInvoiceSidebar"
    @invoice-success="handleInvoiceSuccess"
  />
</template>

<script setup lang="ts">
import { toast, type ToastOptions } from 'vue3-toastify'
import InvoiceInformationSidebar from './InvoiceInformationSidebar.vue'
import { formatPriceAmount } from '~/utils/currencyHelpers'
import IconFile from '~/assets/account/file-earmark-medical-fill.svg?component'
import IconReview from '~/assets/account/chat-quote.svg?component'
import IconDownload from '~/assets/account/cloud-download-fill.svg?component'
import IconChevronDown from '~/assets/icons/chevron-down.svg?component'

const { t, locale } = useI18n()

const { getAccessToken } = useAuthTokens()

const { orderItem, orderId, hasCustomerInvoice, orderCreatedAt } = defineProps<{
  orderItem: any
  orderId: string
  hasCustomerInvoice?: boolean
  orderCreatedAt: string
}>()

const emit = defineEmits<{
  'invoice-success': [data?: { has_customer_invoice: boolean }]
}>()

const handleInvoiceSuccess = (data?: { has_customer_invoice: boolean }) => {
  // Update the order item's has_customer_invoice status if provided
  if (data?.has_customer_invoice) {
    // We need to emit this up to the parent to update the order
    emit('invoice-success', data)
  } else {
    emit('invoice-success')
  }
}

const isExpanded = ref(false)
const isInvoiceSidebarOpen = ref(false)

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

const openInvoiceSidebar = () => {
  isInvoiceSidebarOpen.value = true
}

const closeInvoiceSidebar = () => {
  isInvoiceSidebarOpen.value = false
}

const isItemVisible = computed(() => {
  if (orderItem.type === 'ADD_ON') return false

  return orderItem.status !== 'CANCELED' || orderItem.review_url
})

const isOrderAfterInvoiceCutoff = computed(() => {
  // This is when we started creating invoices ourselves
  // We can't provide invoices for orders before this date
  const cutoffDate = new Date('2023-09-01')
  const orderDate = new Date(orderCreatedAt)
  return orderDate >= cutoffDate
})

const getImageLink = computed(() => {
  const id = orderItem.media_id

  if (!id) return '/_nuxt3-static/promo-cards/giftbox.webp'
  return `https://media.gokonfetti.com/${id}/200x0/${id}_order.webp`
})

const handleInvoiceAction = () => {
  if (hasCustomerInvoice) {
    downloadInvoice()
  } else {
    openInvoiceSidebar()
  }
}

const downloadInvoice = async () => {
  try {
    const token = await getAccessToken()
    const config = useRuntimeConfig()
    const { apiBaseURL } = config.public

    const response = await fetch(
      `${apiBaseURL}/v1/checkout/orders/${orderId}/download-file`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'INVOICE',
          order_item_id: orderItem.id,
        }),
      }
    )

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `invoice-${orderItem.id}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  } catch (error) {
    toast(t('general.error'), {
      autoClose: 2000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
  }
}

const handleDownloadFile = async () => {
  const fetchLink = `/v1/checkout/orders/${orderId}/items/${orderItem.id}/files`

  try {
    const token = await getAccessToken()
    const response = await kftApiRequest(fetchLink, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (response && Array.isArray(response) && response.length) {
      window.open(response[0], '_blank')
    }
  } catch (error) {
    toast(t('general.error'), {
      autoClose: 2000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
  }
}
</script>

<style lang="postcss" scoped>
.order-item {
  @apply flex flex-col md:flex-row items-start md:items-center gap-2 bg-white p-2.5 mb-1.5 rounded-lg cursor-pointer md:cursor-default;
  box-shadow: 0 4px 16px 0 rgba(31, 42, 55, 0.06);

  &:last-child {
    @apply mb-0;
  }

  &--expanded {
    @apply md:flex-row;
  }

  &__header {
    @apply flex items-center justify-between gap-2 w-full md:w-auto;
  }

  &__image {
    @apply relative min-w-[60px] w-[60px] h-[60px] rounded-lg overflow-hidden;

    img {
      @apply w-full h-full object-cover;
    }
  }

  &__button {
    @apply flex items-center gap-2 text-xs cursor-pointer font-medium p-3 py-2 rounded-lg transition-all duration-300 border border-white;

    :deep(svg) {
      @apply min-w-4 min-h-4;
    }

    &:hover {
      @apply border-teal-900;
    }

    &--primary {
      @apply border-teal-900;

      :deep(svg path) {
        @apply transition-all duration-300;
      }

      &:hover {
        @apply bg-teal-900 text-white;

        :deep(svg path) {
          @apply fill-white;
        }
      }
    }

    &--text {
      @apply text-teal-900 border md:border-white  border-teal-900;

      :deep(svg path) {
        @apply fill-teal-900 transition-all duration-300;
      }

      &:hover {
        @apply border border-teal-900;
      }
    }
  }

  &__quantity {
    @apply absolute right-0 bottom-0 bg-primary text-white text-sm px-3 py-1;
    border-top-left-radius: 8px;
  }

  &__date {
    @apply text-sm;
  }

  &__canceled {
    @apply rounded-lg bg-red-500 text-white text-xs p-1 inline-flex ml-auto my-auto;
  }

  &__type {
    @apply absolute left-0 top-0 bg-primary text-white text-sm px-3 py-1;
    border-bottom-right-radius: 8px;
  }

  &__content {
    @apply flex flex-row gap-1 justify-between items-start w-full;
  }

  &__main {
    @apply flex flex-row gap-1;
  }

  &__content-info {
    @apply pl-2 min-w-40 md:max-w-72;
  }

  &__title {
    @apply font-medium text-xs;
  }

  &__price {
    @apply font-normal mt-auto text-xs text-gray-600;
  }

  &__chevron {
    @apply flex items-start justify-center w-6 h-6 text-gray-500 md:hidden pt-1;

    :deep(svg) {
      @apply w-4 h-4;
    }
  }

  &__actions {
    @apply w-full md:w-auto ml-auto  grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-3;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.5s ease-in-out;

    @media (max-width: 768px) {
      .order-item--expanded & {
        max-height: 200px;
      }
    }

    @media (min-width: 768px) {
      max-height: none;
      overflow: visible;
    }

    .order-item__button--full-width {
      @apply col-span-2 md:col-span-1;
    }
  }
}
</style>
